import React, { useState, useEffect, useRef } from 'react';
import { getImageUrl } from '../utils/imageUtils';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, User, MessageCircle, Search, Settings, LogOut, Shield, Crown, Bell, Eye, Users, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MobileMenu } from './MobileMenu';
import { MobileNavigation } from './MobileNavigation';
import { MobileChatDropdown } from './MobileChatDropdown';
import { toast } from 'sonner';

export const Navbar = () => {
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatDropdownOpen, setChatDropdownOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadVisits, setUnreadVisits] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Close user menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      fetchUnreadNotifications();
      fetchUnreadVisits();
      const interval = setInterval(() => {
        fetchUnreadCount();
        fetchUnreadNotifications();
        fetchUnreadVisits();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setChatDropdownOpen(false);
  }, [location.pathname]);

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/messages');
      const total = response.data.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
      setUnreadCount(total);
    } catch (error) {
      // Silently fail
    }
  };

  const fetchUnreadNotifications = async () => {
    try {
      const response = await api.get('/notifications/unread-count');
      setUnreadNotifications(response.data.count || 0);
    } catch (error) {
      // Silently fail
    }
  };

  const fetchUnreadVisits = async () => {
    try {
      const response = await api.get('/visits/unread-count');
      setUnreadVisits(response.data.count || 0);
    } catch (error) {
      // Silently fail
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete('/account');
      toast.success('Votre compte a été supprimé');
      logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression du compte');
    } finally {
      setDeleting(false);
      setConfirmDeleteDialogOpen(false);
    }
  };

  const handleNavClick = (e, targetPath) => {
    if (location.pathname === targetPath) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (targetPath === '/feed') {
      sessionStorage.removeItem('feedScrollPosition');
    }
  };

  return (
    <>
      <nav className="fixed top-0 w-full z-50 bg-dark/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Mobile: Hamburger Menu Button */}
            <button 
              className="md:hidden p-2 -ml-2 text-gray-400 hover:text-gold transition-colors"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="hamburger-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 md:gap-3" data-testid="logo-link">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center">
                <span className="text-dark font-serif text-lg md:text-xl">D</span>
              </div>
              <span className="font-serif text-xl md:text-2xl gold-gradient-text tracking-tight hidden sm:block">
                DiscreetLux
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              {user ? (
                <>
                  <Link 
                    to="/feed" 
                    className="text-gray-400 hover:text-gold transition-colors duration-300 text-sm uppercase tracking-widest"
                    data-testid="nav-feed"
                    onClick={(e) => handleNavClick(e, '/feed')}
                  >
                    Fil d'actualité
                  </Link>
                  <Link 
                    to="/search" 
                    className="text-gray-400 hover:text-gold transition-colors duration-300 text-sm uppercase tracking-widest"
                    data-testid="nav-search"
                    onClick={(e) => handleNavClick(e, '/search')}
                  >
                    Rechercher
                  </Link>
                  <Link 
                    to="/messages" 
                    className="relative text-gray-400 hover:text-gold transition-colors duration-300 text-sm uppercase tracking-widest"
                    data-testid="nav-messages"
                    onClick={(e) => handleNavClick(e, '/messages')}
                  >
                    Messages
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-3 w-5 h-5 bg-gold text-dark text-xs font-bold rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Link>
                  
                  {user.is_subscribed && (
                    <Link 
                      to="/visits" 
                      className="relative text-gray-400 hover:text-gold transition-colors duration-300 text-sm uppercase tracking-widest flex items-center gap-1"
                      data-testid="nav-visits"
                      onClick={(e) => handleNavClick(e, '/visits')}
                    >
                      <Eye className="w-4 h-4" />
                      Visites
                      {unreadVisits > 0 && (
                        <span className="ml-1 min-w-[20px] h-5 px-1.5 bg-gold text-dark text-xs font-bold rounded-full flex items-center justify-center">
                          {unreadVisits > 9 ? '9+' : unreadVisits}
                        </span>
                      )}
                    </Link>
                  )}
                  
                  {user.is_subscribed && (
                    <Link 
                      to="/notifications" 
                      className="relative text-gray-400 hover:text-gold transition-colors duration-300 text-sm uppercase tracking-widest flex items-center gap-1"
                      data-testid="nav-notifications"
                      onClick={(e) => handleNavClick(e, '/notifications')}
                    >
                      <Bell className="w-4 h-4" />
                      Notifs
                      {unreadNotifications > 0 && (
                        <span className="absolute -top-1 -right-3 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                          {unreadNotifications > 9 ? '9+' : unreadNotifications}
                        </span>
                      )}
                    </Link>
                  )}
                  
                  {user.is_subscribed && (
                    <Link 
                      to="/validation" 
                      className="text-gray-400 hover:text-gold transition-colors duration-300 text-sm uppercase tracking-widest"
                      data-testid="nav-validation"
                      onClick={(e) => handleNavClick(e, '/validation')}
                    >
                      Validation
                    </Link>
                  )}
                  
                  <div className="relative" ref={userMenuRef}>
                    <button 
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className="flex items-center gap-2 text-gray-400 hover:text-gold transition-colors"
                      data-testid="user-menu-trigger"
                    >
                      <div className="w-8 h-8 rounded-full bg-dark-subtle border border-gold/30 flex items-center justify-center overflow-hidden">
                        {user.profile_picture ? (
                          <img 
                            src={getImageUrl(user.profile_picture)} 
                            alt="" 
                            className="w-full h-full object-cover" 
                          />
                        ) : (
                          <User className="w-4 h-4" />
                        )}
                      </div>
                      <span className="text-sm hidden lg:block">{user.pseudo}</span>
                      {user.is_subscribed && <Crown className="w-4 h-4 text-gold" />}
                    </button>
                    
                    {userMenuOpen && (
                      <div className="absolute right-0 top-full mt-2 w-56 bg-dark-paper border border-white/10 rounded-md shadow-xl z-50 py-1">
                        <Link to="/profile" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-gold transition-colors" data-testid="dropdown-profile">
                          <User className="w-4 h-4" /> Mon Profil
                        </Link>
                        <Link to="/settings" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-gold transition-colors" data-testid="dropdown-settings">
                          <Settings className="w-4 h-4" /> Paramètres
                        </Link>
                        <Link to="/relations" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-gold transition-colors" data-testid="dropdown-relations">
                          <Users className="w-4 h-4" /> Mes Relations
                        </Link>
                        {!user.is_subscribed && (
                          <Link to="/subscription" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-gold hover:bg-white/5 transition-colors" data-testid="dropdown-subscription">
                            <Crown className="w-4 h-4" /> Abonnez-vous
                          </Link>
                        )}
                        {user.is_admin && (
                          <>
                            <div className="border-t border-white/10 my-1" />
                            <Link to="/admin" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm text-gold hover:bg-white/5 transition-colors" data-testid="dropdown-admin">
                              <Shield className="w-4 h-4" /> Administration
                            </Link>
                          </>
                        )}
                        <div className="border-t border-white/10 my-1" />
                        <button onClick={() => { setUserMenuOpen(false); handleLogout(); }} className="flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-white/5 transition-colors w-full text-left" data-testid="dropdown-logout">
                          <LogOut className="w-4 h-4" /> Déconnexion
                        </button>
                        <button onClick={() => { setUserMenuOpen(false); setDeleteDialogOpen(true); }} className="flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-white/5 transition-colors w-full text-left" data-testid="dropdown-delete-account">
                          <Trash2 className="w-4 h-4" /> Supprimer mon compte
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Delete Account Dialog - Native */}
                  {deleteDialogOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                      <div className="absolute inset-0 bg-black/80" onClick={() => setDeleteDialogOpen(false)} />
                      <div className="relative bg-dark-paper border border-white/10 rounded-lg max-w-md w-full p-6 shadow-xl">
                        <h2 className="text-white text-lg font-semibold flex items-center gap-2 mb-4">
                          <AlertTriangle className="w-5 h-5 text-red-500" />
                          Supprimer votre compte ?
                        </h2>
                        <div className="text-gray-400 text-sm space-y-3 mb-6">
                          <p>Êtes-vous sûr de vouloir supprimer définitivement votre compte DiscreetLux ?</p>
                          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400 text-sm space-y-2">
                            <p className="font-semibold">Cette action est DÉFINITIVE et IRRÉVERSIBLE :</p>
                            <ul className="list-disc list-inside space-y-1 text-red-400/90">
                              <li>Toutes vos photos et vidéos seront supprimées</li>
                              <li>Vos messages et conversations seront perdus</li>
                              <li>Votre abonnement sera résilié immédiatement sans remboursement</li>
                              <li>Vous ne pourrez pas récupérer votre compte</li>
                            </ul>
                          </div>
                        </div>
                        <div className="flex gap-3 justify-end">
                          <button onClick={() => setDeleteDialogOpen(false)} className="px-4 py-2 bg-dark-subtle text-white border border-white/10 rounded-md text-sm hover:bg-white/10">Annuler</button>
                          <button onClick={() => { setDeleteDialogOpen(false); setConfirmDeleteDialogOpen(true); }} className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700">Je comprends, continuer</button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Second Confirmation Dialog - Native */}
                  {confirmDeleteDialogOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                      <div className="absolute inset-0 bg-black/80" onClick={() => !deleting && setConfirmDeleteDialogOpen(false)} />
                      <div className="relative bg-dark-paper border border-red-500/30 rounded-lg max-w-md w-full p-6 shadow-xl">
                        <h2 className="text-red-500 text-lg font-semibold flex items-center gap-2 mb-4">
                          <Trash2 className="w-5 h-5" />
                          Dernière confirmation
                        </h2>
                        <div className="text-gray-400 text-sm mb-6">
                          <p className="mb-4">Vous êtes sur le point de supprimer définitivement votre compte <strong className="text-white">{user?.pseudo}</strong>.</p>
                          <p className="text-red-400 font-semibold">Cette action ne peut pas être annulée.</p>
                        </div>
                        <div className="flex gap-3 justify-end">
                          <button onClick={() => setConfirmDeleteDialogOpen(false)} disabled={deleting} className="px-4 py-2 bg-dark-subtle text-white border border-white/10 rounded-md text-sm hover:bg-white/10 disabled:opacity-50">Non, garder mon compte</button>
                          <button onClick={handleDeleteAccount} disabled={deleting} className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-testid="confirm-delete-account-btn">
                            {deleting ? (
                              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Suppression...</>
                            ) : (
                              'Oui, supprimer mon compte'
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="text-gray-400 hover:text-gold transition-colors duration-300 text-sm uppercase tracking-widest"
                    data-testid="nav-login"
                  >
                    Connexion
                  </Link>
                  <Link 
                    to="/register" 
                    className="btn-gold text-sm"
                    data-testid="nav-register"
                  >
                    S'inscrire
                  </Link>
                </>
              )}
            </div>

            {/* Mobile: Right side buttons */}
            <div className="flex items-center gap-2 md:hidden">
              {user ? (
                <>
                  {/* Chat dropdown button */}
                  <button 
                    className="p-2 text-gray-400 hover:text-gold transition-colors relative"
                    onClick={() => setChatDropdownOpen(!chatDropdownOpen)}
                    data-testid="mobile-chat-btn"
                  >
                    <Users className="w-5 h-5" />
                    <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full" />
                  </button>
                  
                  {/* User avatar */}
                  <Link to="/profile" className="p-1">
                    <div className="w-8 h-8 rounded-full bg-dark-subtle border border-gold/30 flex items-center justify-center overflow-hidden">
                      {user.profile_picture ? (
                        <img 
                          src={getImageUrl(user.profile_picture)} 
                          alt="" 
                          className="w-full h-full object-cover" 
                        />
                      ) : (
                        <User className="w-4 h-4 text-gold" />
                      )}
                    </div>
                  </Link>
                </>
              ) : (
                <Link 
                  to="/login" 
                  className="btn-gold text-sm px-4 py-2"
                  data-testid="mobile-login-btn"
                >
                  Connexion
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
      
      {/* Mobile Chat Dropdown */}
      <MobileChatDropdown isOpen={chatDropdownOpen} onClose={() => setChatDropdownOpen(false)} />
      
      {/* Mobile Bottom Navigation */}
      {user && (
        <MobileNavigation 
          unreadCount={unreadCount} 
          unreadNotifications={unreadNotifications} 
        />
      )}
    </>
  );
};
